import React from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { UserIcon } from '@hero/web-icons';
import { Button } from '../../../ui/Button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../ui/Form/Form';
import { Spinner } from '../../Spinner';
import { IconInput } from './IconInput';

export interface IndividualCustomerStepValues {
  name: string;
}

export interface IndividualCustomerStepProps {
  /** Called when the user submits — wizard collects payload and calls `submit` from the manager. */
  onSubmit: (values: IndividualCustomerStepValues) => void | Promise<void>;
  onBack: () => void;
  isPending: boolean;
}

/**
 * Step 2 (Individual Customer branch). Just collects the user's
 * display name; the unified `auth.signUp` is called with
 * `personType: 'individual_customer'`.
 */
export function IndividualCustomerStep({
  onSubmit,
  onBack,
  isPending,
}: IndividualCustomerStepProps) {
  const { t } = useTranslation('m-auth');

  const schema = z.object({
    name: z.string().trim().min(1, { message: t('form.name.validation') }),
  });
  const form = useForm<IndividualCustomerStepValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { name: '' },
  });

  return (
    <Form {...form}>
      <form className="space-y-5" onSubmit={form.handleSubmit(onSubmit)}>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {t('individual_customer.step_title')}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {t('individual_customer.step_subtitle')}
          </p>
        </div>

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('form.name.title')}</FormLabel>
              <FormControl>
                <IconInput
                  icon={<UserIcon size="small" />}
                  placeholder={t('form.name.placeholder')}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex items-center justify-between gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onBack} disabled={isPending}>
            {t('buttons.back')}
          </Button>
          <Button
            type="submit"
            disabled={isPending || !form.formState.isValid}
            className="flex-1">
            {isPending ? <Spinner /> : t('buttons.create_account')}
          </Button>
        </div>
      </form>
    </Form>
  );
}
